/**
 * Mobility API Client
 *
 * @remarks
 * Cliente HTTP para interactuar con los endpoints del módulo Mobility.
 * Maneja autenticación automáticamente usando el token del store.
 *
 * @module Mobility/API
 */

import axios, { type AxiosInstance } from 'axios';
import type {
  TransitStop,
  CreateStopDto,
  UpdateStopDto,
  StopFilters,
} from '../types/stop.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Crea una instancia de axios con configuración base
 */
function createApiInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: `${API_URL}/mobility`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Interceptor para agregar token automáticamente
  instance.interceptors.request.use(
    (config) => {
      // Obtener token del localStorage (donde lo guarda useAuthStore)
      const authStore = localStorage.getItem('auth-storage');
      if (authStore) {
        try {
          const parsed = JSON.parse(authStore);
          const token = parsed.state?.token;
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('[MobilityAPI] Error parsing auth token:', error);
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Interceptor para manejar errores comunes
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.error('[MobilityAPI] Unauthorized - token may be expired');
        // Aquí podrías emitir un evento para forzar logout
      }
      return Promise.reject(error);
    }
  );

  return instance;
}

const api = createApiInstance();

/**
 * Cliente API para gestión de paradas de transporte público
 */
export const mobilityApi = {
  /**
   * Stops - Paradas de transporte público
   */
  stops: {
    /**
     * Obtiene todas las paradas de un proyecto
     *
     * @param filters - Filtros de búsqueda
     * @returns Array de paradas
     *
     * @example
     * ```typescript
     * const stops = await mobilityApi.stops.getAll({ projectId: 'abc-123' });
     * ```
     */
    async getAll(filters: StopFilters): Promise<TransitStop[]> {
      const response = await api.get<TransitStop[]>('/stops', {
        params: filters,
      });
      return response.data;
    },

    /**
     * Obtiene una parada por ID
     *
     * @param id - ID de la parada
     * @returns Parada
     *
     * @example
     * ```typescript
     * const stop = await mobilityApi.stops.getById('abc-123');
     * ```
     */
    async getById(id: string): Promise<TransitStop> {
      const response = await api.get<TransitStop>(`/stops/${id}`);
      return response.data;
    },

    /**
     * Crea una nueva parada
     *
     * @param data - Datos de la parada
     * @returns Parada creada
     *
     * @example
     * ```typescript
     * const newStop = await mobilityApi.stops.create({
     *   name: 'Parada Brown y Chiclana',
     *   code: 'BB-001',
     *   type: 'bus',
     *   geometry: { type: 'Point', coordinates: [-68.53, -31.54] },
     *   projectId: 'abc-123',
     *   accessible: true,
     * });
     * ```
     */
    async create(data: CreateStopDto): Promise<TransitStop> {
      const response = await api.post<TransitStop>('/stops', data);
      return response.data;
    },

    /**
     * Actualiza una parada existente
     *
     * @param id - ID de la parada
     * @param data - Datos a actualizar
     * @returns Parada actualizada
     *
     * @example
     * ```typescript
     * const updatedStop = await mobilityApi.stops.update('abc-123', {
     *   name: 'Nuevo nombre',
     *   accessible: true,
     * });
     * ```
     */
    async update(id: string, data: UpdateStopDto): Promise<TransitStop> {
      const response = await api.patch<TransitStop>(`/stops/${id}`, data);
      return response.data;
    },

    /**
     * Elimina una parada
     *
     * @param id - ID de la parada
     *
     * @example
     * ```typescript
     * await mobilityApi.stops.delete('abc-123');
     * ```
     */
    async delete(id: string): Promise<void> {
      await api.delete(`/stops/${id}`);
    },
  },

  /**
   * Routes - Rutas de transporte público (futuro)
   */
  routes: {
    // TODO: Implementar en FASE futura
  },

  /**
   * Bikeways - Ciclovías (futuro)
   */
  bikeways: {
    // TODO: Implementar en FASE futura
  },
};

/**
 * Helper para manejar errores de API
 *
 * @param error - Error de axios
 * @returns Mensaje de error amigable
 */
export function getMobilityApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const message = error.response?.data?.message;

    // Si el backend envía un mensaje, usarlo
    if (message && typeof message === 'string') {
      return message;
    }

    // Traducir códigos de estado comunes
    switch (status) {
      case 400:
        return 'Los datos ingresados no son válidos';
      case 401:
        return 'No estás autenticado';
      case 403:
        return 'No tienes permisos para realizar esta acción';
      case 404:
        return 'La parada no fue encontrada';
      case 409:
        return 'Ya existe una parada con ese código en este proyecto';
      case 500:
        return 'Error en el servidor';
      default:
        return 'Error de conexión con el servidor';
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Ha ocurrido un error inesperado';
}
